import React, {
    FC,
    MutableRefObject,
    TextareaHTMLAttributes,
    useEffect,
    useMemo,
    useState
} from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import Typography from '@components/common/typography';

interface IProps {
    value?: string;
    onChange?: (value: string) => void;
    helperText?: string;
    error?: boolean;
    label?: string;
    placeholder?: string;
    className?: string;
    variant?: 'small' | 'large';
    full?: boolean;
    disabled?: boolean;
    stringClassName?: string;
    ref?: MutableRefObject<null>;
    inputClassName?: string;
    wrapperRef?: MutableRefObject<null>;
    maxLength?: number;
}

const cx = classNames.bind(styles);

const Textarea: FC<IProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'>> = ({
    value = '',
    onChange,
    placeholder,
    variant = 'small',
    label,
    prefix,
    helperText,
    maxLength,
    full,
    className,
    stringClassName,
    error,
    disabled,
    ref,
    inputClassName,
    wrapperRef,
    ...props
}) => {
    const [inputId] = useState(String(props.id ? props.id : Math.random()));
    const [localValue, setLocalValue] = useState('');
    const rootStyles = useMemo(
        () =>
            cx([
                styles.root,
                className,
                variant,
                {
                    disabled,
                    full,
                    error
                }
            ]),
        [full, error, disabled, className, prefix, variant]
    );

    const labelStyles = useMemo(() => cx([styles.label, { isActive: !!value }]), [value]);

    useEffect(() => {
        if (value !== undefined) {
            setLocalValue(value);
        }
    }, [value]);

    const helperAlignment = useMemo(() => {
        return !!maxLength && helperText
            ? 'justify-between'
            : !!maxLength && !helperText
              ? 'flex-end'
              : 'flex-start';
    }, [maxLength, helperText]);

    return (
        <div className={`${rootStyles} ${stringClassName}`} ref={wrapperRef}>
            {label && (
                <label htmlFor={inputId} className={labelStyles}>
                    {label}
                </label>
            )}
            <div className={styles.input_wrapper}>
                <textarea
                    disabled={disabled}
                    placeholder={placeholder}
                    value={localValue}
                    ref={ref}
                    id={inputId}
                    maxLength={maxLength}
                    className={inputClassName}
                    onChange={(e: any) => {
                        if (!!maxLength) {
                            if (e.target.value.length <= maxLength) {
                                onChange && onChange(e.target.value);
                                setLocalValue(e.target.value);
                            }
                        } else {
                            onChange && onChange(e.target.value);
                            setLocalValue(e.target.value);
                        }
                    }}
                    {...props}
                />
            </div>
            <div className={styles.helper_wrapper} style={{ justifyContent: helperAlignment }}>
                <Typography variant="body3" className={styles.helper_text}>
                    {helperText} {helperText} {helperText}
                </Typography>
                {!!maxLength && (
                    <Typography variant="body3" className={styles.counter}>
                        {value.length}/{maxLength}
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default Textarea;
